import type React from "react";
import type { FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { useController, useFormContext, useWatch } from "react-hook-form";

import type { MessageTypeDto } from "@/api/types";
import { MentionComposer, type MentionComposerValue } from "@/components/CommunityFeed/MentionComposer";
import { FieldCharacterCount } from "@/design-system/FieldFeedback";

import { FormErrorWrapper } from "./FormErrorWrapper";

interface FormMentionComposerProps<TFormValues extends FieldValues> {
  name: FieldPath<TFormValues>;
  contentTemplateName: FieldPath<TFormValues>;
  mentionsName: FieldPath<TFormValues>;
  rules?: RegisterOptions<TFormValues>;
  placeholder?: string;
  messageId?: string;
  groupId?: string;
  projectConnectionId?: string;
  hideFromAdmins?: boolean;
  type?: MessageTypeDto;
  size?: "compact" | "large";
  /** Passed to the underlying mention composer input. */
  id?: string;
  /** Passed to the underlying mention composer input for stable test selection. */
  "data-testid"?: string;
  /** Passed to the underlying mention composer input to associate descriptive feedback. */
  "aria-describedby"?: string;
}

/**
 * Binds string content, optional template text, and mention-array fields as one react-hook-form composer value.
 * The optional `id` and `aria-describedby` props are passed through to the underlying mention composer input.
 */
export function FormMentionComposer<TFormValues extends FieldValues>({
  name,
  contentTemplateName,
  mentionsName,
  rules,
  ...props
}: FormMentionComposerProps<TFormValues>): React.ReactNode {
  const form = useFormContext<TFormValues>();
  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ name, rules, control: form.control });
  const contentTemplate = useWatch({ name: contentTemplateName, control: form.control });
  const mentions = useWatch({ name: mentionsName, control: form.control });
  const value: MentionComposerValue = {
    content: field.value ?? "",
    contentTemplate,
    mentions,
  };

  const maxLengthValue = typeof rules?.maxLength === "object" ? rules.maxLength.value : undefined;

  return (
    <FormErrorWrapper
      name={name}
      subtext={
        maxLengthValue != null && <FieldCharacterCount current={value.content.length} maximum={maxLengthValue} />
      }
    >
      <MentionComposer
        {...props}
        maxLength={maxLengthValue}
        ref={ref}
        name={field.name}
        value={value}
        invalid={fieldState.invalid}
        onBlur={field.onBlur}
        onChange={(next) => {
          field.onChange(next.content);
          form.setValue(contentTemplateName, next.contentTemplate as never, { shouldDirty: true });
          form.setValue(mentionsName, next.mentions as never, { shouldDirty: true });
        }}
      />
    </FormErrorWrapper>
  );
}
